import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ComponentType, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useParams } from "react-router";
import { ApiRequestError } from "./api";
import { ignorePasswordManagers } from "./formFill";
import { ButtonLink, StatePanel, ToastProvider } from "./kajeet";
import { PORTAL_TITLE } from "./title";
// The Kajeet UI Kit first (kit.css declares the layer order), then this app's own styles.
import "./kajeet/kit.css";
import "./kajeet/tokens.css";
import "./kajeet/base.css";
import "./kajeet/components.css";
import "./styles.css";
import "./desk.css";
import "./pages/portal/portal.css";
import "./pages/desk/work.css";
import "./pages/desk/manage.css";
import "./pages/desk/accounts.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      // Don't hammer the API on 4xx; retry network blips and 5xx once.
      retry: (count, err) => !(err instanceof ApiRequestError && err.status >= 400 && err.status < 500) && count < 1,
    },
  },
});

// Every page is its own chunk (route `lazy`): customers on /portal download the portal pages and
// shared bits only, never the staff workspace (and staff don't download the portal).
type Mod = Record<string, unknown>;
const RELOADED = "kajeet-service:chunk-reload";

/** After a deploy an open tab asks for chunk hashes that no longer exist: reload once to pick up the
 *  new build instead of showing an error. */
async function loadChunk<M>(load: () => Promise<M>): Promise<M> {
  try {
    const m = await load();
    try {
      sessionStorage.removeItem(RELOADED);
    } catch {}
    return m;
  } catch (err) {
    let again = false;
    try {
      again = !sessionStorage.getItem(RELOADED);
      sessionStorage.setItem(RELOADED, "1");
    } catch {}
    if (again) {
      window.location.reload();
      return new Promise<M>(() => {});
    }
    throw err;
  }
}

const page =
  <M extends Mod>(load: () => Promise<M>, name: keyof M, errorName?: keyof M) =>
  async () => {
    const m = await loadChunk(load);
    return { Component: m[name] as ComponentType, ...(errorName ? { ErrorBoundary: m[errorName] as ComponentType } : {}) };
  };
const portal = () => import("./pages/portal/PortalCases");
const kb = () => import("./pages/portal/PortalKb");
const help = () => import("./pages/portal/PortalHelp");
const standalone = () => import("./pages/portal/PortalStandalone");
const knowledge = () => import("./pages/desk/Knowledge");
const accounts = () => import("./pages/desk/Accounts");

/** A staff page behind a minimum role. */
const gated = (min: "supervisor" | "admin", load: () => Promise<{ C: ComponentType }>) => async () => {
  const [{ RequireRole }, { C }] = await Promise.all([loadChunk(() => import("./components/Layout")), loadChunk(load)]);
  return {
    Component: () => (
      <RequireRole min={min}>
        <C />
      </RequireRole>
    ),
  };
};

function Blank() {
  return <div className="route-loading" aria-busy="true" />;
}

const router = createBrowserRouter([
  // support.kajeet.com on its own is the customer portal; staff go to /desk (server.ts redirects too).
  { path: "/", element: <Navigate to="/portal" replace /> },
  { path: "/signed-out", lazy: page(() => import("./pages/SignedOut"), "SignedOutPage"), hydrateFallbackElement: <Blank /> },
  { path: "/portal/signed-out", lazy: page(standalone, "PortalSignedOutPage"), hydrateFallbackElement: <Blank /> },
  { path: "/portal/link-expired", lazy: page(standalone, "PortalLinkExpiredPage"), hydrateFallbackElement: <Blank /> },
  // The public contact form: no sign-in, outside the portal shell.
  { path: "/portal/contact", lazy: page(() => import("./pages/portal/PortalContact"), "PortalContactPage"), hydrateFallbackElement: <Blank /> },
  // The case-closed survey's landing page (DWR-538): no sign-in either.
  { path: "/portal/survey/:token", lazy: page(() => import("./pages/portal/PortalSurvey"), "PortalSurveyPage"), hydrateFallbackElement: <Blank /> },
  // Public help articles ('public' knowledge base): no sign-in either.
  { path: "/portal/help", lazy: page(help, "PortalHelpPage"), hydrateFallbackElement: <Blank /> },
  { path: "/portal/help/topics/:id", lazy: page(help, "PortalHelpTopicPage"), hydrateFallbackElement: <Blank /> },
  { path: "/portal/help/:ref", lazy: page(help, "PortalHelpArticlePage"), hydrateFallbackElement: <Blank /> },
  // Customer portal (Sentinel IdP sign-in or a signed case link); no staff chrome.
  {
    path: "/portal",
    lazy: page(() => import("./pages/portal/PortalLayout"), "PortalLayout", "PortalRouteError"),
    hydrateFallbackElement: <Blank />,
    children: [
      { index: true, lazy: page(portal, "PortalCasesPage") },
      { path: "new", lazy: page(() => import("./pages/portal/PortalNewCase"), "PortalNewCasePage") },
      { path: "cases/:id", lazy: page(() => import("./pages/portal/PortalCase"), "PortalCasePage") },
      { path: "kb", lazy: page(kb, "PortalKbPage") },
      { path: "kb/topics/:id", lazy: page(kb, "PortalKbTopicPage") },
      { path: "kb/:number", lazy: page(kb, "PortalKbArticlePage") },
      { path: "*", Component: NotFoundPortal },
    ],
  },
  // Staff workspace (Microsoft sign-in, auth.ts).
  {
    path: "/",
    lazy: page(() => import("./components/Layout"), "Layout", "RouteError"),
    hydrateFallbackElement: <Blank />,
    children: [
      { path: "desk", lazy: page(() => import("./pages/desk/Queues"), "QueuesPage") },
      { path: "desk/new", lazy: page(() => import("./pages/desk/NewCase"), "NewCasePage") },
      { path: "desk/cases/:id", lazy: page(() => import("./pages/desk/CaseWork"), "CaseWorkPage") },
      { path: "desk/chats", lazy: page(() => import("./pages/desk/Chats"), "ChatsPage") },
      { path: "desk/kb", lazy: page(knowledge, "KnowledgePage") },
      { path: "desk/kb/topics", lazy: page(() => import("./pages/desk/KbTopics"), "KbTopicsPage") },
      { path: "desk/kb/quality", lazy: page(() => import("./pages/desk/KbQuality"), "KbQualityPage") },
      { path: "desk/kb/:number", lazy: page(knowledge, "KnowledgeArticlePage") },
      // KB editor: supervisors and above, like its API.
      { path: "desk/kb/new", lazy: gated("supervisor", () => import("./pages/desk/KbEditor").then((m) => ({ C: m.KbNewPage }))) },
      { path: "desk/kb/:articleId/edit", lazy: gated("supervisor", () => import("./pages/desk/KbEditor").then((m) => ({ C: m.KbEditPage }))) },
      { path: "desk/accounts", lazy: page(accounts, "AccountsPage") },
      { path: "desk/accounts/:id", lazy: page(accounts, "AccountDetailPage") },
      { path: "desk/preferences", lazy: page(() => import("./pages/desk/Preferences"), "PreferencesPage") },
      // Reports (DWR-546): one list of saved reports and dashboards, any staff member. A report opens run full
      // width (Edit opens the builder at /edit); New opens the builder. Survey rows stay supervisor-only server-side. The builder's old address redirects.
      { path: "desk/reports", lazy: page(() => import("./pages/desk/ReportBuilder"), "ReportLibraryPage") },
      { path: "desk/reports/new", lazy: page(() => import("./pages/desk/ReportBuilder"), "ReportBuilderPage") },
      { path: "desk/reports/:id", lazy: page(() => import("./pages/desk/ReportBuilder"), "ReportViewPage") },
      { path: "desk/reports/:id/edit", lazy: page(() => import("./pages/desk/ReportBuilder"), "ReportBuilderPage") },
      { path: "desk/dashboards/:id", lazy: page(() => import("./pages/desk/ReportBuilder"), "DashboardPage") },
      { path: "desk/report-builder", element: <ToReports /> },
      { path: "desk/report-builder/:id", element: <ToReports /> },
      // The retired Scorecard and Overview pages open the starter dashboard that has their reports.
      { path: "desk/scorecard", lazy: page(() => import("./pages/desk/ReportBuilder"), "RetiredPageRedirect") },
      { path: "desk/saved-replies", lazy: page(() => import("./pages/desk/SavedReplies"), "SavedRepliesPage") },
      { path: "desk/overview", lazy: page(() => import("./pages/desk/ReportBuilder"), "RetiredPageRedirect") },
      { path: "desk/surveys", lazy: gated("supervisor", () => import("./pages/desk/Surveys").then((m) => ({ C: m.SurveysPage }))) },
      // Parked and skipped inbound email: supervisors and above, like its API.
      { path: "desk/email-exceptions", lazy: gated("supervisor", () => import("./pages/desk/EmailExceptions").then((m) => ({ C: m.EmailExceptionsPage }))) },
      { path: "desk/admin", lazy: gated("admin", () => import("./pages/desk/Admin").then((m) => ({ C: m.AdminPage }))) },
      { path: "*", lazy: page(() => import("./pages/NotFound"), "NotFoundPage") },
    ],
  },
]);

/** /desk/report-builder[/:id] (old links, emails sent before the move) to /desk/reports[/:id]. */
function ToReports() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/desk/reports${id ? `/${id}` : ""}${search}`} replace />;
}

function NotFoundPortal() {
  return (
    <StatePanel kind="info" icon="search" title="Page not found" headingLevel="h1" action={<ButtonLink href="/portal">Go to your cases</ButtonLink>}>
      This address doesn't match a page in Kajeet Support Access.
    </StatePanel>
  );
}

// Before the first paint and before the lazy portal chunks arrive.
if (window.location.pathname === "/portal" || window.location.pathname.startsWith("/portal/")) document.title = PORTAL_TITLE;

const root = document.getElementById("root");
if (!root) throw new Error("#root missing from index.html");
// DWR-616: no password lives here; keep password managers from decorating (and shifting) the fields.
ignorePasswordManagers();

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
